import React from 'react';
import { Box, BoxProps, useColorModeValue, forwardRef } from '@chakra-ui/react';

interface ModernCardProps extends BoxProps {
  children: React.ReactNode;
  variant?: 'default' | 'elevated' | 'outlined';
  hover?: boolean;
}

const ModernCard = forwardRef<ModernCardProps, 'div'>(
  ({ children, variant = 'default', hover = false, ...props }, ref) => {
    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.700');
    const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');

    const getVariantStyles = () => {
      switch (variant) {
        case 'elevated':
          return {
            bg: bgColor,
            boxShadow: `0 4px 6px -1px ${shadowColor}, 0 2px 4px -1px ${shadowColor}`,
            border: 'none',
          };
        case 'outlined':
          return {
            bg: 'transparent',
            border: '1px',
            borderColor: borderColor,
            boxShadow: 'none',
          };
        default:
          return {
            bg: bgColor,
            boxShadow: `0 1px 3px 0 ${shadowColor}, 0 1px 2px 0 ${shadowColor}`,
            border: '1px',
            borderColor: borderColor,
          };
      }
    };

    const baseStyles = {
      borderRadius: 'xl',
      transition: 'all 0.2s ease-in-out',
      ...getVariantStyles(),
    };

    const hoverStyles = hover
      ? {
          _hover: {
            transform: 'translateY(-2px)',
            boxShadow: `0 10px 15px -3px ${shadowColor}, 0 4px 6px -2px ${shadowColor}`,
          },
        }
      : {};

    return (
      <Box ref={ref} {...baseStyles} {...hoverStyles} {...props}>
        {children}
      </Box>
    );
  }
);

ModernCard.displayName = 'ModernCard';

export default ModernCard;
